import { createContext, useState, useContext, useCallback, useMemo } from 'react';

const ToastContext = createContext(null);

let toastId = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  // Explicit dismiss by id — shared between the button handler and the
  // auto-dismiss timer so both code paths go through a single function.
  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const addToast = useCallback((message, type = 'info', duration) => {
    const id = ++toastId;

    // Error toasts default to 8 s — users need more time to read an error and
    // decide whether to act on it. All other types stay at 4 s. A caller can
    // still override by passing an explicit duration argument.
    // Pass `null` or `0` to opt out of auto-dismiss entirely.
    const effectiveDuration = duration !== undefined
      ? duration
      : type === 'error' ? 8000 : 4000;

    setToasts(prev => [...prev, { id, message, type }]);

    // Only schedule auto-dismiss when a positive duration is supplied.
    if (effectiveDuration) {
      setTimeout(() => dismissToast(id), effectiveDuration);
    }
  }, [dismissToast]);

  const toast = useMemo(() => ({
    success: (msg, duration) => addToast(msg, 'success', duration),
    error:   (msg, duration) => addToast(msg, 'error',   duration),
    info:    (msg, duration) => addToast(msg, 'info',    duration),
    warn:    (msg, duration) => addToast(msg, 'warning', duration),
  }), [addToast]);

  return (
    <ToastContext.Provider value={toast}>
      {children}

      {/* Toast container — fixed to viewport top-right, stacks vertically */}
      <div
        aria-live="polite"
        aria-atomic="false"
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          maxWidth: '400px',
          width: 'calc(100% - 40px)',
        }}
      >
        {toasts.map(t => (
          <div
            key={t.id}
            // Only error toasts use role="alert" (assertive aria-live).
            // Success, warning, and info use role="status" (polite) so screen
            // readers finish the current sentence before announcing the toast,
            // reducing interruption for non-critical feedback.
            role={t.type === 'error' ? 'alert' : 'status'}
            style={{
              // `position: relative` lets us absolutely position the dismiss
              // button in the top-right corner without disrupting text flow.
              position: 'relative',
              padding: '14px 44px 14px 20px', // right padding reserves space for the × button
              borderRadius: '10px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              animation: 'toastSlideIn 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              fontSize: '14px',
              fontWeight: '500',
              ...(t.type === 'success' ? {
                background: '#f0fff4', border: '1px solid #c6f6d5', color: '#276749',
              } : t.type === 'error' ? {
                background: '#fff5f5', border: '1px solid #fed7d7', color: '#9b2c2c',
              } : t.type === 'warning' ? {
                background: '#fffaf0', border: '1px solid #feebc8', color: '#9c4221',
              } : {
                background: '#ebf8ff', border: '1px solid #bee3f8', color: '#2b6cb0',
              }),
            }}
          >
            {/* aria-hidden prevents screen readers from announcing raw
                Unicode characters ("check mark", "multiplication x", etc.)
                that add no information beyond what the role/message already
                conveys. The visual icon is purely decorative here. */}
            <span aria-hidden="true" style={{ fontSize: '18px', flexShrink: 0 }}>
              {t.type === 'success' ? '✓' : t.type === 'error' ? '✕' : t.type === 'warning' ? '!' : 'ℹ'}
            </span>

            <span style={{ flex: 1 }}>{t.message}</span>

            {/* Dismiss button — sits in the top-right corner via absolute
                positioning. The 44 × 44 px minimum touch target is met by the
                combined padding on the element itself. `color: inherit` keeps
                it visually consistent across all toast variants without
                hard-coding per-type colours. */}
            <button
              type="button"
              aria-label="Dismiss notification"
              onClick={() => dismissToast(t.id)}
              style={{
                position: 'absolute',
                top: '4px',
                right: '4px',
                // Ensure the touch target reaches 44 × 44 px on mobile
                // (Fitts's Law + WCAG 2.5.5 Target Size).
                minWidth: '36px',
                minHeight: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'inherit',
                opacity: 0.7,
                fontSize: '18px',
                padding: '4px 8px',
                borderRadius: '4px',
                // Visible focus ring for keyboard / switch-access users.
                // outline is handled via the global :focus-visible rule but we
                // reinforce it here so the button is never invisible on focus.
                lineHeight: 1,
              }}
              // Surface a focus ring on keyboard navigation without showing it
              // on mouse clicks — handled by onFocus/onBlur rather than CSS
              // because inline styles cannot target pseudo-classes.
              onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
              onMouseLeave={e => { e.currentTarget.style.opacity = '0.7'; }}
              onFocus={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.outline = '2px solid currentColor'; e.currentTarget.style.outlineOffset = '2px'; }}
              onBlur={e => { e.currentTarget.style.opacity = '0.7'; e.currentTarget.style.outline = 'none'; }}
            >
              {/* aria-hidden: the button's aria-label already conveys the action */}
              <span aria-hidden="true">×</span>
            </button>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes toastSlideIn {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
